// components/employee/MyStatsComponent.js
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { TrendingUp, Target, Award, Calendar } from 'lucide-react';
import employeeService from '../../services/employeeService';

const MyStatsComponent = () => {
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // Début du mois
    endDate: new Date().toISOString().split('T')[0] // Aujourd'hui
  });

  useEffect(() => {
    loadMyStats();
  }, []);

  const loadMyStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await employeeService.getMyPersonalStats(
        period.startDate || null,
        period.endDate || null
      );
      
      if (response.success) {
        setMyStats(response.data);
      } else {
        setError(response.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (field, value) => {
    setPeriod(prev => ({ ...prev, [field]: value }));
  };

  const applyPeriod = () => {
    loadMyStats();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F CFA';
  };

  const getPerformanceColor = (ranking, teamSize) => {
    const percentage = ranking / teamSize;
    if (percentage <= 0.2) return 'success'; // Top 20%
    if (percentage <= 0.5) return 'info'; // Top 50%
    if (percentage <= 0.8) return 'warning'; // Top 80%
    return 'danger'; // Bottom 20%
  };

  const getPerformanceText = (ranking, teamSize) => {
    const percentage = ranking / teamSize;
    if (percentage <= 0.2) return 'Excellent';
    if (percentage <= 0.5) return 'Bon';
    if (percentage <= 0.8) return 'Moyen';
    return 'À améliorer';
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">Chargement de mes statistiques...</p>
      </div>
    );
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Mes Statistiques Personnelles</h5>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={5}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={period.startDate}
                    onChange={(e) => handlePeriodChange('startDate', e.target.value)}
                  />
                </Col>
                <Col md={5}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={period.endDate}
                    onChange={(e) => handlePeriodChange('endDate', e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <button 
                    className="btn btn-primary btn-sm w-100"
                    onClick={applyPeriod}
                  >
                    OK
                  </button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {myStats && (
            <Row>
              {/* Statistiques de vente */}
              <Col lg={3} md={6} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <TrendingUp size={40} className="text-success mb-3" />
                    <h3 className="fw-bold text-success">{myStats.totalSales}</h3>
                    <p className="text-muted mb-0">Ventes réalisées</p>
                    <small className="text-muted">
                      Chiffre d'affaires: {formatCurrency(myStats.totalRevenue)}
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Valeur moyenne */}
              <Col lg={3} md={6} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <Calendar size={40} className="text-info mb-3" />
                    <h3 className="fw-bold text-info">{formatCurrency(myStats.averageSaleValue)}</h3>
                    <p className="text-muted mb-0">Valeur moyenne</p>
                    <small className="text-muted">par vente</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Classement */}
              <Col lg={3} md={6} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <Award size={40} className={`text-${getPerformanceColor(myStats.ranking, myStats.teamSize)} mb-3`} />
                    <h3 className={`fw-bold text-${getPerformanceColor(myStats.ranking, myStats.teamSize)}`}>
                      #{myStats.ranking}
                    </h3>
                    <p className="text-muted mb-0">Classement équipe</p>
                    <small className="text-muted">
                      {getPerformanceText(myStats.ranking, myStats.teamSize)} ({myStats.ranking}/{myStats.teamSize})
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Objectif */}
              <Col lg={3} md={6} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <Target size={40} className="text-warning mb-3" />
                    <h3 className="fw-bold text-warning">
                      {Math.round(myStats.progressPercent)}%
                    </h3>
                    <p className="text-muted mb-0">Objectif atteint</p>
                    <small className="text-muted">
                      {formatCurrency(myStats.achieved)} / {formatCurrency(myStats.target)}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Barre de progression détaillée */}
          {myStats && (
            <Row className="mt-4">
              <Col>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Progression vers l'objectif</span>
                      <span className="fw-bold">{Math.round(myStats.progressPercent)}%</span>
                    </div>
                    <ProgressBar 
                      now={Math.min(myStats.progressPercent, 100)} 
                      variant={myStats.progressPercent >= 100 ? 'success' : 
                              myStats.progressPercent >= 75 ? 'info' :
                              myStats.progressPercent >= 50 ? 'warning' : 'danger'}
                      style={{ height: '8px' }}
                    />
                    <div className="d-flex justify-content-between mt-2">
                      <small className="text-muted">
                        Réalisé: {formatCurrency(myStats.achieved)}
                      </small>
                      <small className="text-muted">
                        Restant: {formatCurrency(Math.max(0, (myStats.target || 0) - (myStats.achieved || 0)))}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default MyStatsComponent;